import { useCallback, useMemo } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage, PostStatus } from "@/core/types/mediaTypes";
import { MediaContentType } from "@/core/types/mediaContentType";
import { useMediaQuery, mediaCacheUtils } from "./useMediaQuery";

/**
 * Media library state machine.
 *
 * Drive discovery was removed: the nightly sync job is now the sole writer
 * from Drive to the database, so this hook only reads from the database via
 * React Query. See `backend/jobs/scheduled_tasks/drive_sync_task.py`.
 */
type MediaLibraryState =
  | "idle"
  | "loading"
  | "loadingMore"
  | "loaded"
  | "error";

interface UseSimpleMediaLibraryOptions {
  clientId: string;
  autoLoad?: boolean;
  stageFilter?: MediaStage[];
  excludePostStatuses?: PostStatus[];
  contentTypes?: MediaContentType[];
  onlyNonLeadCarousel?: boolean;
  websiteTags?: string[];
}

/**
 * Hook that exposes the client's media library backed by React Query.
 *
 * Returns the paginated slices per content type plus the usual loading/error
 * flags. No side effects trigger Drive scans.
 */
export const useSimpleMediaLibrary = (
  options: UseSimpleMediaLibraryOptions
) => {
  const {
    clientId,
    stageFilter,
    excludePostStatuses,
    contentTypes = ["photos", "stories", "reels", "videos", "post_video", "audio"],
    onlyNonLeadCarousel,
    websiteTags,
  } = options;

  const {
    data: mediaData,
    isLoading: isQueryLoading,
    error: queryError,
    refetch,
    hasMore: queryHasMore,
    loadMore: queryLoadMore,
    isLoadingMore: queryIsLoadingMore,
  } = useMediaQuery(clientId, {
    stageFilter,
    excludePostStatuses,
    contentTypes,
    onlyNonLeadCarousel,
    websiteTags,
  });

  const state: MediaLibraryState = useMemo(() => {
    if (isQueryLoading) return "loading";
    if (queryError) return "error";
    if (mediaData) return "loaded";
    return "idle";
  }, [isQueryLoading, queryError, mediaData]);

  const items = useMemo(
    () => ({
      images: mediaData?.images || [],
      shortVideos: mediaData?.shortVideos || [],
      longVideos: mediaData?.longVideos || [],
      videos: mediaData?.videos || [],
      postVideos: mediaData?.postVideos || [],
      audio: mediaData?.audio || [],
    }),
    [mediaData]
  );

  const refresh = useCallback(() => {
    mediaCacheUtils.invalidateAllMedia(clientId);
  }, [clientId]);

  const invalidateCache = useCallback(() => {
    refresh();
  }, [refresh]);

  const removeItem = useCallback(
    (itemId: number) => {
      mediaCacheUtils.removeMediaItem(clientId, itemId);
    },
    [clientId]
  );

  const updateItem = useCallback(
    (updatedItem: MediaItem) => {
      mediaCacheUtils.upsertMediaItem(clientId, updatedItem);
    },
    [clientId]
  );

  const hasData =
    items.images.length > 0 ||
    items.shortVideos.length > 0 ||
    items.longVideos.length > 0 ||
    items.videos.length > 0 ||
    items.postVideos.length > 0 ||
    items.audio.length > 0;

  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Failed to load media"
    : null;

  return {
    ...items,

    state,
    error,
    hasMore: queryHasMore,
    offset: 0,
    isLoading: isQueryLoading,
    isLoadingMore: queryIsLoadingMore,
    isIdle: state === "idle",
    hasData,

    loadFromDatabase: refetch,
    loadMore: () => queryLoadMore(),
    refresh,
    invalidateCache,
    removeItem,
    updateItem,

    contentTypeCounts: {
      images: items.images.length,
      stories: items.shortVideos.length,
      reels: items.longVideos.length,
      videos: items.videos.length,
      audio: items.audio.length,
    },
  };
};
